import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Select, message, Switch, InputNumber } from 'antd';
import { UploadOutlined, SaveOutlined, ArrowLeftOutlined, CloseOutlined, LockOutlined, UnlockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  createBlogPost, 
  updateBlogPost, 
  getBlogPostById,
  validateSlug 
} from '../../../api/blogApi';
import { IMAGE_CLOUD_NAME, IMAGE_CLOUD_PRESET, IMAGE_CLOUD_FOLDER } from '../../../settings';
import '../../../assets/scss/components/BlogPost/blog.scss';

const { Option } = Select;
const { TextArea } = Input;

const BlogForm = () => {
  const history = useHistory();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [featuredImageUrl, setFeaturedImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [existingBlogPost, setExistingBlogPost] = useState(null);
  const [slugValidating, setSlugValidating] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [customSlugEnabled, setCustomSlugEnabled] = useState(false);
  const isEditMode = !!id;
  const titleDebounceRef = React.useRef(null);
  const slugDebounceRef = React.useRef(null);

  useEffect(() => {
    let isMounted = true;

    if (isEditMode) {
      fetchBlogPost(isMounted);
    }

    return () => {
      isMounted = false;
      // Cleanup debounce timers
      if (titleDebounceRef.current) {
        clearTimeout(titleDebounceRef.current);
      }
      if (slugDebounceRef.current) {
        clearTimeout(slugDebounceRef.current);
      }
    };
  }, [id]);

  const fetchBlogPost = async (isMounted) => {
    try {
      const data = await getBlogPostById(id);
      
      if (!isMounted) return;
      
      setExistingBlogPost(data);
      form.setFieldsValue({
        title: data.title,
        slug: data.slug,
        author: data.author,
        excerpt: data.excerpt,
        tags: data.tags,
        categories: data.categories,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        metaKeywords: data.metaKeywords,
        published: data.published,
        order: data.order,
      });
      setContent(data.content || '');
      setFeaturedImageUrl(data.featuredImage || '');
    } catch (error) {
      if (isMounted) {
        message.error('Failed to fetch blog post');
        console.error(error);
      }
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    
    // Clear previous timeout
    if (titleDebounceRef.current) {
      clearTimeout(titleDebounceRef.current);
    }
    
    // Debounce slug generation to avoid blocking on every keystroke
    // Only auto-generate if custom slug is not enabled
    if (!customSlugEnabled && (!isEditMode || !form.getFieldValue('slug'))) {
      titleDebounceRef.current = setTimeout(() => {
        const generatedSlug = generateSlug(title);
        form.setFieldsValue({ slug: generatedSlug });
        if (generatedSlug) {
          checkSlugAvailability(generatedSlug);
        }
      }, 300);
    }
  };

  const checkSlugAvailability = async (slug) => {
    if (!slug) {
      setSlugError('');
      return;
    }

    setSlugValidating(true);
    setSlugError('');

    try {
      const result = await validateSlug(slug, isEditMode ? id : null);
      if (!result.available) {
        setSlugError('This slug is already taken. Please use a different one.');
      } else {
        setSlugError('');
      }
    } catch (error) {
      console.error('Error validating slug:', error);
    } finally {
      setSlugValidating(false);
    }
  };

  const handleSlugChange = (e) => {
    const slug = e.target.value;
    
    // Clear previous timeout
    if (slugDebounceRef.current) {
      clearTimeout(slugDebounceRef.current);
    }
    
    if (slug) {
      // Debounce slug validation
      slugDebounceRef.current = setTimeout(() => {
        checkSlugAvailability(slug);
      }, 500);
    } else {
      setSlugError('');
    }
  };

  const handleImageUpload = () => {
    const cloudinaryWidget = window.cloudinary.createUploadWidget(
      {
        cloudName: IMAGE_CLOUD_NAME,
        uploadPreset: IMAGE_CLOUD_PRESET,
        folder: `${IMAGE_CLOUD_FOLDER}/blog`,
        sources: ['local', 'url', 'camera'],
        multiple: false,
        maxFiles: 1,
        clientAllowedFormats: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
        maxFileSize: 10000000, // 10MB
        resourceType: 'image',
      },
      (error, result) => {
        if (!error && result && result.event === 'success') {
          const imageUrl = result.info.secure_url;
          setFeaturedImageUrl(imageUrl);
          message.success('Image uploaded successfully');
        } else if (error) {
          message.error('Failed to upload image');
          console.error(error);
        }
      }
    );
    cloudinaryWidget.open();
  };

  const handleSubmit = async (values) => {
    if (!content || content === '<p><br></p>') {
      message.error('Please add content to the blog post');
      return;
    }

    if (slugError) {
      message.error('Please fix the slug error before submitting');
      return;
    }

    setLoading(true);
    try {
      const blogData = {
        ...values,
        content,
        featuredImage: featuredImageUrl,
      };

      // Set publishedDate to current date/time if published is true
      if (values.published) {
        blogData.publishedDate = new Date().toISOString();
      }

      if (isEditMode) {
        // For update, merge with existing blog post to preserve all fields
        const updateData = {
          ...existingBlogPost,
          ...blogData,
          id: id,
          version: existingBlogPost.version
        };
        await updateBlogPost(updateData, existingBlogPost.version);
        message.success('Blog post updated successfully');
      } else {
        await createBlogPost(blogData);
        message.success('Blog post created successfully');
      }
      
      history.push('/dashboard/blog-posts');
    } catch (error) {
      if (error.response?.status === 409) {
        message.error('This slug already exists. Please use a different slug.');
        setSlugError('This slug is already taken. Please use a different one.');
      } else {
        message.error(isEditMode ? 'Failed to update blog post' : 'Failed to create blog post');
      }
      console.error('Error saving blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  const quillModules = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  function imageHandler() {
    const quill = this.quill;
    const range = quill.getSelection();
    
    const cloudinaryWidget = window.cloudinary.createUploadWidget(
      {
        cloudName: IMAGE_CLOUD_NAME,
        uploadPreset: IMAGE_CLOUD_PRESET,
        folder: `${IMAGE_CLOUD_FOLDER}/blog`,
        sources: ['local', 'url', 'camera'],
        multiple: false,
        maxFiles: 1,
        clientAllowedFormats: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
        maxFileSize: 10000000,
        resourceType: 'image',
      },
      (error, result) => {
        if (!error && result && result.event === 'success') {
          const imageUrl = result.info.secure_url;
          quill.insertEmbed(range.index, 'image', imageUrl);
          message.success('Image inserted successfully');
        } else if (error) {
          message.error('Failed to upload image');
          console.error(error);
        }
      }
    );
    cloudinaryWidget.open();
  }

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  return (
    <div className="blog-form-container">
      <div className="blog-form-header">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => history.push('/dashboard/blog-posts')}
          style={{ marginBottom: 16 }}
        >
          Back to List
        </Button>
        <h2>{isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="blog-form"
        initialValues={{
          published: false,
          order: 0,
        }}
      >
        <div className="form-section">
          <div className="section-title">Basic Information</div>
          
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please enter blog title' }]}
          >
            <Input 
              placeholder="Enter blog post title" 
              size="large"
              onChange={handleTitleChange}
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                Slug (URL)
                <Button 
                  type="link" 
                  size="small" 
                  icon={customSlugEnabled ? <UnlockOutlined /> : <LockOutlined />}
                  onClick={() => setCustomSlugEnabled(!customSlugEnabled)}
                  style={{ marginLeft: 8, padding: 0 }}
                >
                  {customSlugEnabled ? 'Custom Slug Enabled' : 'Enable Custom Slug'}
                </Button>
              </span>
            }
            name="slug"
            rules={[
              { required: true, message: 'Please enter slug' },
              { pattern: /^[a-z0-9-]+$/, message: 'Slug must contain only lowercase letters, numbers, and hyphens' }
            ]}
            validateStatus={slugValidating ? 'validating' : slugError ? 'error' : ''}
            help={
              slugValidating ? 'Checking availability...' : 
              slugError ? slugError :
              customSlugEnabled ? (
                <span>
                  <InfoCircleOutlined style={{ marginRight: 4 }} />
                  Best format: lowercase, use hyphens (-) between words, no spaces or special characters. Example: "my-blog-post-title"
                </span>
              ) : 
              'Auto-generated from title. Enable custom slug to edit manually.'
            }
          >
            <Input 
              placeholder="blog-post-url" 
              onChange={handleSlugChange}
              disabled={!customSlugEnabled}
            />
          </Form.Item>

          <Form.Item
            label="Author"
            name="author"
            rules={[{ required: true, message: 'Please enter author name' }]}
          >
            <Input placeholder="Author name" />
          </Form.Item>

          <Form.Item
            label="Excerpt"
            name="excerpt"
            rules={[{ required: true, message: 'Please enter excerpt' }]}
          >
            <TextArea 
              rows={3} 
              placeholder="Short description for preview" 
              maxLength={300}
              showCount
            />
          </Form.Item>
        </div>

        <div className="form-section">
          <div className="section-title">Content</div>
          
          <Form.Item label="Content">
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={quillModules}
              formats={quillFormats}
              className="quill-editor"
              placeholder="Write your blog content here..."
            />
          </Form.Item>
        </div>

        <div className="form-section image-upload-section">
          <div className="section-title">Featured Image</div>
          
          <Button 
            icon={<UploadOutlined />} 
            onClick={handleImageUpload}
          >
            Upload Featured Image
          </Button>
          
          {featuredImageUrl && (
            <div className="featured-image-preview">
              <img src={featuredImageUrl} alt="Featured" />
              <Button
                type="text"
                danger
                icon={<CloseOutlined />}
                onClick={() => setFeaturedImageUrl('')}
                className="remove-image-btn"
              >
                Remove
              </Button>
            </div>
          )}
        </div>

        {/* <div className="form-section">
          <div className="section-title">Categories & Tags</div>
          
          <Form.Item
            label="Categories"
            name="categories"
          >
            <Select
              mode="tags"
              placeholder="Add categories"
              style={{ width: '100%' }}
            >
              <Option value="Technology">Technology</Option>
              <Option value="Business">Business</Option>
              <Option value="Lifestyle">Lifestyle</Option>
              <Option value="News">News</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Tags"
            name="tags"
          >
            <Select
              mode="tags"
              placeholder="Add tags"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </div> */}

        <div className="form-section">
          <div className="section-title">SEO Settings</div>
          
          <Form.Item
            label="Meta Title"
            name="metaTitle"
          >
            <Input placeholder="SEO title (leave empty to use post title)" />
          </Form.Item>

          <Form.Item
            label="Meta Description"
            name="metaDescription"
          >
            <TextArea 
              rows={2} 
              placeholder="SEO description"
              maxLength={160}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="Meta Keywords"
            name="metaKeywords"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  
                  // Check if keywords are comma-separated
                  const keywords = value.split(',').map(k => k.trim()).filter(k => k);
                  
                  if (keywords.length === 0) {
                    return Promise.reject(new Error('Please enter at least one keyword'));
                  }
                  
                  // Check for invalid characters in each keyword
                  const invalidKeyword = keywords.find(k => /[^a-zA-Z0-9\s-]/.test(k));
                  if (invalidKeyword) {
                    return Promise.reject(new Error(`Invalid keyword "${invalidKeyword}". Use only letters, numbers, spaces, and hyphens.`));
                  }
                  
                  return Promise.resolve();
                }
              }
            ]}
            help="Enter comma-separated keywords (e.g., workspace, coworking, office space)"
          >
            <Input placeholder="workspace, coworking, office space" />
          </Form.Item>
        </div>

        <div className="form-section">
          <div className="section-title">Settings</div>
          
          <Form.Item
            label="Published"
            name="published"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label="Display Order"
            name="order"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </div>

        <div className="form-actions">
          <Button 
            type="primary" 
            htmlType="submit" 
            icon={<SaveOutlined />}
            loading={loading}
            size="large"
          >
            {isEditMode ? 'Update Post' : 'Create Post'}
          </Button>
          <Button 
            onClick={() => history.push('/dashboard/blog-posts')}
            size="large"
          >
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default BlogForm;
